export type CellValue = string | number | boolean | null;
export type CellGrid = Readonly<Record<string, CellValue | undefined>>;

export class FormulaError extends Error {
  constructor(
    public readonly code:
      | "#DIV/0!"
      | "#VALUE!"
      | "#NAME?"
      | "#REF!"
      | "#N/A"
      | "#NUM!",
    message: string = code,
  ) {
    super(message === code ? code : `${code} ${message}`);
    this.name = "FormulaError";
  }
}

type TokenKind =
  | "number"
  | "string"
  | "identifier"
  | "operator"
  | "comma"
  | "lparen"
  | "rparen"
  | "colon"
  | "eof";

type Token = { kind: TokenKind; text: string; value?: number | string };

type Expression =
  | { kind: "literal"; value: CellValue }
  | { kind: "reference"; address: string }
  | { kind: "range"; from: string; to: string }
  | { kind: "unary"; operator: "+" | "-" | "%"; operand: Expression }
  | { kind: "binary"; operator: string; left: Expression; right: Expression }
  | { kind: "call"; name: string; args: Expression[] };

type RangeValue = { kind: "range-value"; values: CellValue[]; rows: number; columns: number };
type RuntimeValue = CellValue | RangeValue;

const CELL_REFERENCE = /^\$?([A-Z]+)\$?([1-9][0-9]*)$/i;

function normalizeAddress(address: string): string {
  const match = CELL_REFERENCE.exec(address);
  if (!match) throw new FormulaError("#REF!", `Invalid cell reference: ${address}`);
  return `${match[1].toUpperCase()}${Number(match[2])}`;
}

function columnNumber(label: string): number {
  let result = 0;
  for (const character of label) result = result * 26 + character.charCodeAt(0) - 64;
  return result;
}

function columnLabel(index: number): string {
  let result = "";
  while (index > 0) {
    index -= 1;
    result = String.fromCharCode(65 + (index % 26)) + result;
    index = Math.floor(index / 26);
  }
  return result;
}

function coordinates(address: string): [number, number] {
  const match = CELL_REFERENCE.exec(normalizeAddress(address));
  if (!match) throw new FormulaError("#REF!");
  return [columnNumber(match[1].toUpperCase()), Number(match[2])];
}

function tokenize(input: string): Token[] {
  const source = input.trim().startsWith("=") ? input.trim().slice(1) : input.trim();
  const tokens: Token[] = [];
  let index = 0;

  while (index < source.length) {
    const character = source[index];
    if (/\s/.test(character)) {
      index += 1;
      continue;
    }
    if (character === '"') {
      let value = "";
      index += 1;
      let closed = false;
      while (index < source.length) {
        if (source[index] === '"' && source[index + 1] === '"') {
          value += '"';
          index += 2;
        } else if (source[index] === '"') {
          index += 1;
          closed = true;
          break;
        } else {
          value += source[index++];
        }
      }
      if (!closed) throw new FormulaError("#VALUE!", "Unterminated string");
      tokens.push({ kind: "string", text: value, value });
      continue;
    }
    if (/[0-9.]/.test(character)) {
      const match = /^(?:\d+(?:\.\d*)?|\.\d+)(?:[Ee][+-]?\d+)?/.exec(source.slice(index));
      if (!match) throw new FormulaError("#VALUE!");
      tokens.push({ kind: "number", text: match[0], value: Number(match[0]) });
      index += match[0].length;
      continue;
    }
    if (/[A-Za-z_$]/.test(character)) {
      const match = /^[A-Za-z_$][A-Za-z0-9_$]*/.exec(source.slice(index));
      if (!match) throw new FormulaError("#NAME?");
      tokens.push({ kind: "identifier", text: match[0] });
      index += match[0].length;
      continue;
    }
    const twoCharacters = source.slice(index, index + 2);
    if (["<=", ">=", "<>"].includes(twoCharacters)) {
      tokens.push({ kind: "operator", text: twoCharacters });
      index += 2;
      continue;
    }
    const punctuation: Partial<Record<string, TokenKind>> = {
      ",": "comma",
      ";": "comma",
      "(": "lparen",
      ")": "rparen",
      ":": "colon",
    };
    const punctuationKind = punctuation[character];
    if (punctuationKind) {
      tokens.push({ kind: punctuationKind, text: character });
      index += 1;
      continue;
    }
    if ("+-*/^%&=<>".includes(character)) {
      tokens.push({ kind: "operator", text: character });
      index += 1;
      continue;
    }
    throw new FormulaError("#VALUE!", `Unexpected character: ${character}`);
  }
  tokens.push({ kind: "eof", text: "" });
  return tokens;
}

class Parser {
  private position = 0;

  constructor(private readonly tokens: Token[]) {}

  parse(): Expression {
    const result = this.parseComparison();
    if (this.peek().kind !== "eof") throw new FormulaError("#VALUE!", `Unexpected token: ${this.peek().text}`);
    return result;
  }

  private peek(): Token {
    return this.tokens[this.position];
  }

  private consume(kind?: TokenKind, text?: string): Token {
    const token = this.peek();
    if ((kind && token.kind !== kind) || (text && token.text !== text)) {
      throw new FormulaError("#VALUE!", `Expected ${text ?? kind ?? "token"}`);
    }
    this.position += 1;
    return token;
  }

  private parseComparison(): Expression {
    let expression = this.parseConcatenation();
    while (this.peek().kind === "operator" && ["=", "<>", "<", ">", "<=", ">="].includes(this.peek().text)) {
      const operator = this.consume("operator").text;
      expression = { kind: "binary", operator, left: expression, right: this.parseConcatenation() };
    }
    return expression;
  }

  private parseConcatenation(): Expression {
    let expression = this.parseAdditive();
    while (this.peek().kind === "operator" && this.peek().text === "&") {
      expression = {
        kind: "binary",
        operator: this.consume("operator").text,
        left: expression,
        right: this.parseAdditive(),
      };
    }
    return expression;
  }

  private parseAdditive(): Expression {
    let expression = this.parseMultiplicative();
    while (this.peek().kind === "operator" && ["+", "-"].includes(this.peek().text)) {
      const operator = this.consume("operator").text;
      expression = { kind: "binary", operator, left: expression, right: this.parseMultiplicative() };
    }
    return expression;
  }

  private parseMultiplicative(): Expression {
    let expression = this.parsePower();
    while (this.peek().kind === "operator" && ["*", "/"].includes(this.peek().text)) {
      const operator = this.consume("operator").text;
      expression = { kind: "binary", operator, left: expression, right: this.parsePower() };
    }
    return expression;
  }

  private parsePower(): Expression {
    const left = this.parseUnary();
    if (this.peek().kind === "operator" && this.peek().text === "^") {
      this.consume("operator");
      return { kind: "binary", operator: "^", left, right: this.parsePower() };
    }
    return left;
  }

  private parseUnary(): Expression {
    if (this.peek().kind === "operator" && ["+", "-"].includes(this.peek().text)) {
      return {
        kind: "unary",
        operator: this.consume("operator").text as "+" | "-",
        operand: this.parseUnary(),
      };
    }
    let expression = this.parsePrimary();
    while (this.peek().kind === "operator" && this.peek().text === "%") {
      this.consume();
      expression = { kind: "unary", operator: "%", operand: expression };
    }
    return expression;
  }

  private parsePrimary(): Expression {
    const token = this.peek();
    if (token.kind === "number") {
      this.consume();
      return { kind: "literal", value: token.value as number };
    }
    if (token.kind === "string") {
      this.consume();
      return { kind: "literal", value: token.value as string };
    }
    if (token.kind === "lparen") {
      this.consume();
      const expression = this.parseComparison();
      this.consume("rparen");
      return expression;
    }
    if (token.kind === "identifier") {
      this.consume();
      const name = token.text.toUpperCase();
      if (this.peek().kind === "lparen") {
        this.consume();
        const args: Expression[] = [];
        if (this.peek().kind !== "rparen") {
          do {
            args.push(this.parseComparison());
            if (this.peek().kind !== "comma") break;
            this.consume();
          } while (true);
        }
        this.consume("rparen");
        return { kind: "call", name, args };
      }
      if (name === "TRUE" || name === "FALSE") return { kind: "literal", value: name === "TRUE" };
      if (!CELL_REFERENCE.test(token.text)) throw new FormulaError("#NAME?", `Unknown name: ${token.text}`);
      const from = normalizeAddress(token.text);
      if (this.peek().kind === "colon") {
        this.consume();
        const to = this.consume("identifier").text;
        return { kind: "range", from, to: normalizeAddress(to) };
      }
      return { kind: "reference", address: from };
    }
    throw new FormulaError("#VALUE!", `Unexpected token: ${token.text}`);
  }
}

function scalar(value: RuntimeValue): CellValue {
  if (typeof value === "object" && value !== null && "kind" in value) {
    if (value.values.length !== 1) throw new FormulaError("#VALUE!", "A range cannot be used as a scalar");
    return value.values[0];
  }
  return value;
}

function numeric(value: RuntimeValue): number {
  const primitive = scalar(value);
  if (primitive === null || primitive === "") return 0;
  if (typeof primitive === "boolean") return primitive ? 1 : 0;
  const result = typeof primitive === "number" ? primitive : Number(primitive);
  if (!Number.isFinite(result)) throw new FormulaError("#VALUE!", `Not a number: ${String(primitive)}`);
  return result;
}

function text(value: RuntimeValue): string {
  const primitive = scalar(value);
  if (primitive === null) return "";
  if (typeof primitive === "boolean") return primitive ? "TRUE" : "FALSE";
  return String(primitive);
}

function truthy(value: RuntimeValue): boolean {
  const primitive = scalar(value);
  if (typeof primitive === "boolean") return primitive;
  if (typeof primitive === "number") return primitive !== 0;
  if (primitive === null || primitive === "") return false;
  return true;
}

function flatten(values: RuntimeValue[]): CellValue[] {
  return values.flatMap((value) =>
    typeof value === "object" && value !== null && "kind" in value ? value.values : [value],
  );
}

function comparable(value: CellValue): string | number | boolean {
  if (value === null || value === "") return 0;
  if (typeof value === "string") {
    const number = Number(value);
    return Number.isFinite(number) ? number : value.toLocaleUpperCase();
  }
  return value;
}

function equals(left: CellValue, right: CellValue): boolean {
  return comparable(left) === comparable(right);
}

function compare(left: CellValue, right: CellValue): number {
  const normalizedLeft = comparable(left);
  const normalizedRight = comparable(right);
  if (normalizedLeft === normalizedRight) return 0;
  return normalizedLeft < normalizedRight ? -1 : 1;
}

function wildcardRegex(pattern: string): RegExp {
  let source = "";
  for (let index = 0; index < pattern.length; index += 1) {
    const character = pattern[index];
    if (character === "~" && index + 1 < pattern.length) {
      source += pattern[++index].replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    } else if (character === "*") source += ".*";
    else if (character === "?") source += ".";
    else source += character.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  }
  return new RegExp(`^${source}$`, "i");
}

function matchesCriteria(value: CellValue, criteria: CellValue): boolean {
  if (typeof criteria !== "string") return equals(value, criteria);
  const match = /^(<=|>=|<>|=|<|>)(.*)$/.exec(criteria);
  const operator = match?.[1] ?? "=";
  const operandText = match?.[2] ?? criteria;
  const numericOperand = operandText.trim() === "" ? null : Number(operandText);
  const operand: CellValue = numericOperand !== null && Number.isFinite(numericOperand) ? numericOperand : operandText;
  if ((operandText.includes("*") || operandText.includes("?")) && (operator === "=" || operator === "<>")) {
    const matched = wildcardRegex(operandText).test(text(value));
    return operator === "=" ? matched : !matched;
  }
  const order = compare(value, operand);
  if (operator === "=") return order === 0;
  if (operator === "<>") return order !== 0;
  if (operator === "<") return order < 0;
  if (operator === ">") return order > 0;
  if (operator === "<=") return order <= 0;
  return order >= 0;
}

function requireRange(value: RuntimeValue, name: string): RangeValue {
  if (typeof value !== "object" || value === null || !("kind" in value)) {
    throw new FormulaError("#VALUE!", `${name} requires a range`);
  }
  return value;
}

class Evaluator {
  private readonly cache = new Map<string, CellValue>();
  private readonly active = new Set<string>();
  private readonly normalizedGrid: Record<string, CellValue | undefined>;

  constructor(grid: CellGrid) {
    this.normalizedGrid = {};
    for (const [address, value] of Object.entries(grid)) this.normalizedGrid[normalizeAddress(address)] = value;
  }

  evaluate(expression: Expression): RuntimeValue {
    switch (expression.kind) {
      case "literal":
        return expression.value;
      case "reference":
        return this.cell(expression.address);
      case "range":
        return this.range(expression.from, expression.to);
      case "unary":
        if (expression.operator === "-") return -numeric(this.evaluate(expression.operand));
        if (expression.operator === "%") return numeric(this.evaluate(expression.operand)) / 100;
        return numeric(this.evaluate(expression.operand));
      case "binary":
        return this.binary(expression);
      case "call":
        return this.call(expression);
    }
  }

  cell(address: string): CellValue {
    const normalized = normalizeAddress(address);
    const cached = this.cache.get(normalized);
    if (cached !== undefined || this.cache.has(normalized)) return cached ?? null;
    if (this.active.has(normalized)) throw new FormulaError("#REF!", `Circular reference: ${normalized}`);
    this.active.add(normalized);
    try {
      const raw = this.normalizedGrid[normalized] ?? null;
      const value =
        typeof raw === "string" && raw.trim().startsWith("=")
          ? scalar(this.evaluate(new Parser(tokenize(raw)).parse()))
          : raw;
      this.cache.set(normalized, value);
      return value;
    } finally {
      this.active.delete(normalized);
    }
  }

  private range(from: string, to: string): RangeValue {
    const [fromColumn, fromRow] = coordinates(from);
    const [toColumn, toRow] = coordinates(to);
    const left = Math.min(fromColumn, toColumn);
    const right = Math.max(fromColumn, toColumn);
    const top = Math.min(fromRow, toRow);
    const bottom = Math.max(fromRow, toRow);
    const values: CellValue[] = [];
    for (let row = top; row <= bottom; row += 1) {
      for (let column = left; column <= right; column += 1) {
        values.push(this.cell(`${columnLabel(column)}${row}`));
      }
    }
    return { kind: "range-value", values, rows: bottom - top + 1, columns: right - left + 1 };
  }

  private binary(expression: Extract<Expression, { kind: "binary" }>): CellValue {
    const left = this.evaluate(expression.left);
    const right = this.evaluate(expression.right);
    switch (expression.operator) {
      case "+":
        return numeric(left) + numeric(right);
      case "-":
        return numeric(left) - numeric(right);
      case "*":
        return numeric(left) * numeric(right);
      case "/": {
        const divisor = numeric(right);
        if (divisor === 0) throw new FormulaError("#DIV/0!");
        return numeric(left) / divisor;
      }
      case "^":
        return numeric(left) ** numeric(right);
      case "&":
        return text(left) + text(right);
      case "=":
        return equals(scalar(left), scalar(right));
      case "<>":
        return !equals(scalar(left), scalar(right));
      case "<":
        return compare(scalar(left), scalar(right)) < 0;
      case ">":
        return compare(scalar(left), scalar(right)) > 0;
      case "<=":
        return compare(scalar(left), scalar(right)) <= 0;
      case ">=":
        return compare(scalar(left), scalar(right)) >= 0;
      default:
        throw new FormulaError("#VALUE!");
    }
  }

  private call(expression: Extract<Expression, { kind: "call" }>): RuntimeValue {
    const { name, args } = expression;
    if (name === "IF") {
      if (args.length < 2 || args.length > 3) throw new FormulaError("#VALUE!");
      return truthy(this.evaluate(args[0]))
        ? this.evaluate(args[1])
        : args[2]
          ? this.evaluate(args[2])
          : false;
    }
    if (name === "IFERROR") {
      if (args.length !== 2) throw new FormulaError("#VALUE!");
      try {
        return this.evaluate(args[0]);
      } catch (error) {
        if (!(error instanceof FormulaError)) throw error;
        return this.evaluate(args[1]);
      }
    }

    const values = args.map((argument) => this.evaluate(argument));
    const flat = flatten(values);
    const numericValues = flat
      .filter((value) => value !== null && value !== "" && typeof value !== "boolean")
      .map((value) => Number(value))
      .filter(Number.isFinite);

    switch (name) {
      case "SUM":
        return numericValues.reduce((sum, value) => sum + value, 0);
      case "AVERAGE":
        if (numericValues.length === 0) throw new FormulaError("#DIV/0!");
        return numericValues.reduce((sum, value) => sum + value, 0) / numericValues.length;
      case "MIN":
        return numericValues.length ? Math.min(...numericValues) : 0;
      case "MAX":
        return numericValues.length ? Math.max(...numericValues) : 0;
      case "COUNT":
        return numericValues.length;
      case "SUMIF":
      case "COUNTIF": {
        if (values.length < 2 || values.length > 3) throw new FormulaError("#VALUE!");
        const criteriaRange = requireRange(values[0], name);
        const sumRange = values[2] ? requireRange(values[2], name) : criteriaRange;
        if (sumRange.values.length !== criteriaRange.values.length) throw new FormulaError("#VALUE!");
        let result = 0;
        for (let index = 0; index < criteriaRange.values.length; index += 1) {
          if (matchesCriteria(criteriaRange.values[index], scalar(values[1]))) {
            result += name === "COUNTIF" ? 1 : numeric(sumRange.values[index]);
          }
        }
        return result;
      }
      case "SUMIFS":
      case "COUNTIFS": {
        const isSum = name === "SUMIFS";
        const firstCriteria = isSum ? 1 : 0;
        if (values.length < firstCriteria + 2 || (values.length - firstCriteria) % 2 !== 0) {
          throw new FormulaError("#VALUE!");
        }
        const target = isSum ? requireRange(values[0], name) : requireRange(values[0], name);
        const rowCount = target.values.length;
        const pairs: Array<[RangeValue, CellValue]> = [];
        for (let index = firstCriteria; index < values.length; index += 2) {
          const range = requireRange(values[index], name);
          if (range.values.length !== rowCount) throw new FormulaError("#VALUE!");
          pairs.push([range, scalar(values[index + 1])]);
        }
        let result = 0;
        for (let row = 0; row < rowCount; row += 1) {
          if (pairs.every(([range, criteria]) => matchesCriteria(range.values[row], criteria))) {
            result += isSum ? numeric(target.values[row]) : 1;
          }
        }
        return result;
      }
      case "INDEX": {
        if (values.length < 2 || values.length > 3) throw new FormulaError("#VALUE!");
        const range = requireRange(values[0], name);
        const row = Math.trunc(numeric(values[1]));
        const column = values[2] === undefined ? 1 : Math.trunc(numeric(values[2]));
        if (row < 1 || column < 1 || row > range.rows || column > range.columns) throw new FormulaError("#REF!");
        return range.values[(row - 1) * range.columns + column - 1];
      }
      case "MATCH": {
        if (values.length < 2 || values.length > 3) throw new FormulaError("#VALUE!");
        const lookup = scalar(values[0]);
        const range = requireRange(values[1], name);
        const matchType = values[2] === undefined ? 1 : numeric(values[2]);
        if (range.rows > 1 && range.columns > 1) throw new FormulaError("#N/A");
        if (matchType === 0) {
          const index = range.values.findIndex((value) =>
            typeof lookup === "string" && (lookup.includes("*") || lookup.includes("?"))
              ? wildcardRegex(lookup).test(text(value))
              : equals(value, lookup),
          );
          if (index < 0) throw new FormulaError("#N/A");
          return index + 1;
        }
        let found = -1;
        for (let index = 0; index < range.values.length; index += 1) {
          const order = compare(range.values[index], lookup);
          if ((matchType > 0 && order <= 0) || (matchType < 0 && order >= 0)) found = index;
        }
        if (found < 0) throw new FormulaError("#N/A");
        return found + 1;
      }
      case "TRIM":
        return text(values[0] ?? null).trim().replace(/\s+/g, " ");
      case "LEN":
        return text(values[0] ?? null).length;
      case "LEFT":
        return text(values[0] ?? null).slice(0, values[1] === undefined ? 1 : Math.max(0, numeric(values[1])));
      case "RIGHT": {
        const source = text(values[0] ?? null);
        const length = values[1] === undefined ? 1 : Math.max(0, numeric(values[1]));
        return length === 0 ? "" : source.slice(-length);
      }
      case "MID": {
        const start = Math.trunc(numeric(values[1] ?? null));
        const length = Math.trunc(numeric(values[2] ?? null));
        if (start < 1 || length < 0) throw new FormulaError("#VALUE!");
        return text(values[0] ?? null).slice(start - 1, start - 1 + length);
      }
      case "SUBSTITUTE": {
        const source = text(values[0] ?? null);
        const search = text(values[1] ?? null);
        const replacement = text(values[2] ?? null);
        if (values[3] === undefined) return source.split(search).join(replacement);
        const occurrence = Math.trunc(numeric(values[3]));
        if (occurrence < 1) throw new FormulaError("#VALUE!");
        let seen = 0;
        return source.replaceAll(search, (matched) => (++seen === occurrence ? replacement : matched));
      }
      case "ROUND": {
        const digits = Math.trunc(numeric(values[1] ?? null));
        const factor = 10 ** digits;
        return Math.round((numeric(values[0] ?? null) + Number.EPSILON) * factor) / factor;
      }
      case "ABS":
        return Math.abs(numeric(values[0] ?? null));
      default:
        throw new FormulaError("#NAME?", `Unsupported function: ${name}`);
    }
  }
}

export function evaluateFormula(formula: string, grid: CellGrid = {}): CellValue {
  if (!formula.trim()) return null;
  return scalar(new Evaluator(grid).evaluate(new Parser(tokenize(formula)).parse()));
}

export function evaluateCell(address: string, grid: CellGrid): CellValue {
  return new Evaluator(grid).cell(address);
}

export function tryEvaluateFormula(
  formula: string,
  grid: CellGrid = {},
): { ok: true; value: CellValue } | { ok: false; error: FormulaError } {
  try {
    return { ok: true, value: evaluateFormula(formula, grid) };
  } catch (error) {
    if (error instanceof FormulaError) return { ok: false, error };
    throw error;
  }
}
